import {
  type EditorOption,
  useMultiSelectTypeaheadEditor,
} from 'bundles/personalization-settings/hooks/useMultiSelectTypeaheadEditor';
import { addSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';
import type { SelectedSkill } from 'bundles/personalization-settings/utils/skillsWantedV2';

/** A catalog skill the parent makes available to pick from. */
export type AvailableSkill = { id: string; name: string };

/** Sentinel key for the synthetic "Add '<input>'" option that creates a user-generated skill. */
export const CREATE_USER_GENERATED_KEY = '__personalization_create_user_generated__';

export type SkillOption = EditorOption<SelectedSkill>;

// Module-scope adapters — stable references so the options memo doesn't recompute unnecessarily.
const getItemId = (skill: AvailableSkill) => skill.id;
const getItemName = (skill: AvailableSkill) => skill.name;
const getSelectedCatalogId = (skill: SelectedSkill) => (skill.type === 'catalog' ? skill.id : null);
const getSelectedName = (skill: SelectedSkill) => skill.name;
const makeSelectedFromItem = (skill: AvailableSkill): SelectedSkill => ({
  type: 'catalog',
  id: skill.id,
  name: skill.name,
});
const makeUserGenerated = (name: string): SelectedSkill => ({ type: 'userGenerated', name });

type UseSkillsEditorArgs = {
  value: SelectedSkill[];
  onChange: (skills: SelectedSkill[]) => void;
  availableSkills: readonly AvailableSkill[];
  input: string;
};

export const useSkillsEditor = ({ value, onChange, availableSkills, input }: UseSkillsEditorArgs) => {
  const { options, trimmedInput, addFromOption, addFromInput } = useMultiSelectTypeaheadEditor({
    value,
    onChange,
    availableItems: availableSkills,
    input,
    getItemId,
    getItemName,
    getSelectedCatalogId,
    getSelectedName,
    makeSelectedFromItem,
    makeUserGenerated,
    addItem: addSkill,
    createOptionKey: CREATE_USER_GENERATED_KEY,
  });

  return {
    options,
    trimmedInput,
    addSkillFromOption: addFromOption,
    addSkillFromInput: addFromInput,
  };
};

export default useSkillsEditor;
